import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Eye, Video, Image, Target, Clapperboard, ArrowLeft, CheckCircle, FolderCheck, CalendarClock, History, Undo2 } from 'lucide-react';
import { AnimatedModal } from '../components/AnimatedComponents';
import { useData } from '../contexts/DataContext';
import { Contenuto, StatoContenuto } from '../types';
import StatusBadge from '../components/StatusBadge';
import DriveFlagBadge from '../components/DriveFlagBadge';
import toast from 'react-hot-toast';
import PageHeader from '../components/ui/PageHeader';
import StatsCard from '../components/ui/StatsCard';
import { isPost } from '../utils/tipoContenuto';
import AccountDestinazioneBadge from '../components/AccountDestinazioneBadge';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { appuntamentoDelCliente, valutaRiprese, reelGiratiNelMese, URL_APPUNTAMENTI_RIPRESE } from '../utils/ripreseMatch';
import { usePageState } from '../hooks/usePageState';

const Videomaker: React.FC = () => {
  const navigate = useNavigate();
  const { state, toggleVideoRecorded, updateContenuto, getClienteById, loadContenuti } = useData();
  // Cliente aperto nell'URL: indietro e refresh riportano allo stesso punto.
  // `vista` = '' (da registrare) oppure 'registrati' (girati nel mese).
  const [pageState, setPageState] = usePageState({ client: '', vista: '' }, { push: ['client'] });
  const selectedClientId = pageState.client || null;
  const vistaRegistrati = pageState.vista === 'registrati';
  const [clipConfirm, setClipConfirm] = useState<Contenuto | null>(null);
  const [titoliAppuntamenti, setTitoliAppuntamenti] = useState<string[]>([]);

  // Appuntamenti riprese dei videomaker (calendario iCloud live): servono per
  // sopprimere l'alert "Occorrono subito delle riprese" quando le riprese di
  // quel cliente sono già fissate in agenda. Stessa logica della Dashboard.
  useEffect(() => {
    fetch(URL_APPUNTAMENTI_RIPRESE, {
      headers: { Authorization: `Bearer ${localStorage.getItem('bymarke_token')}` },
    })
      .then((r) => r.json())
      .then((j) => {
        if (j?.success) setTitoliAppuntamenti((j.data.appuntamenti || []).map((a: any) => a.titolo));
      })
      .catch(() => {});
  }, []);

  // Clienti archiviati (status !== 'active'): tutto il loro workflow è escluso e
  // non conteggiato.
  const clientiAttiviIds = new Set(
    state.clienti.filter(c => c.status === 'active').map(c => c.id)
  );

  // Contenuti da registrare: non ancora registrati, in testo pronto / montato / programmato.
  // I post grafici (solo copy + immagine) non si girano: restano fuori.
  const contenutiBase = state.contenuti.filter(
    contenuto => clientiAttiviIds.has(contenuto.clienteId) &&
    !isPost(contenuto) &&
    !contenuto.videoRegistrato && [
      StatoContenuto.TESTO_PRONTO,
      StatoContenuto.MONTATO,
      StatoContenuto.PROGRAMMATO
    ].includes(contenuto.stato)
  );

  const handleMarkAsRegistrato = async (contenuto: Contenuto) => {
    try {
      await toggleVideoRecorded(contenuto.id);
      await loadContenuti();
    } catch (error) {
      console.error('Errore nell\'aggiornamento dello stato:', error);
      toast.error('Errore nell\'aggiornamento. Riprova.');
    }
  };

  // Ripensamento dalla vista "Registrati": il contenuto torna in "Da
  // registrare". Il backend, togliendo "Registrato", azzera anche "Montato"
  // (pagina Editing); programmazione e clip caricate non si toccano.
  const riportaInRiprese = async (contenuto: Contenuto) => {
    try {
      await toggleVideoRecorded(contenuto.id);
      await loadContenuti();
      toast.success('Riportato in "Da registrare"');
    } catch (error) {
      console.error('Errore nel riportare in riprese:', error);
      toast.error('Errore nell\'aggiornamento. Riprova.');
    }
  };

  const applyClipCaricate = async (contenuto: Contenuto, value: boolean) => {
    try {
      await updateContenuto(contenuto.id, { clipCaricate: value });
      await loadContenuti();
    } catch (error) {
      console.error('Errore nell\'aggiornamento clip caricate:', error);
      toast.error('Errore nell\'aggiornamento. Riprova.');
    }
  };

  const handleToggleClipCaricate = (contenuto: Contenuto) => {
    if (!contenuto.clipCaricate) {
      setClipConfirm(contenuto); // apre il modal di conferma
      return;
    }
    applyClipCaricate(contenuto, false); // disattiva direttamente
  };

  const confirmClipCaricate = () => {
    if (!clipConfirm) return;
    const contenuto = clipConfirm;
    setClipConfirm(null);
    applyClipCaricate(contenuto, true);
  };

  const handleSelectClient = (clientId: string, vista: '' | 'registrati' = '') =>
    setPageState({ client: clientId, vista });
  const handleBackToClients = () => setPageState({ client: '', vista: '' });
  const handleCambiaVista = (vista: '' | 'registrati') => setPageState({ vista });

  // Pulsante "Programma": manda il contenuto in "da programmare" ANCHE se non
  // ancora registrato (flag prontoDaProgrammare), poi apre la pagina Programmazione
  // sulla vista "da programmare" filtrata sul cliente, dove si sceglie data e ora.
  // Il contenuto RESTA nella coda "da registrare" del Videomaker (contenutiBase
  // include i non registrati) e NON conta come girato finché non si preme
  // "Registrato". Vedi filtro in Programmazione.tsx (contenutiProntiPerProgrammazione).
  const handleProgramma = async (contenuto: Contenuto) => {
    try {
      // Se era stato tolto dalla coda col pulsante "Rimuovi" della pagina
      // Programmazione, va rimandato lì anche se il flag era già a true: il
      // backend azzera removed_from_scheduling quando riceve ready_to_schedule.
      if (!contenuto.prontoDaProgrammare || contenuto.rimossoDaProgrammazione) {
        await updateContenuto(contenuto.id, { prontoDaProgrammare: true });
        await loadContenuti();
        toast.success('Spostato in "da programmare" — resta comunque da registrare');
      }
      navigate(`/publisher?view=daProgrammare&client=${contenuto.clienteId}`);
    } catch (error) {
      console.error('Errore nello spostare in da programmare:', error);
      toast.error('Errore. Riprova.');
    }
  };

  // Modal di conferma "Clip caricate" (interno, stile app)
  const clipConfirmModal = (
    <AnimatedModal
      isOpen={clipConfirm !== null}
      onClose={() => setClipConfirm(null)}
      title="Clip caricate"
      size="sm"
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200/60 dark:border-emerald-800/40 flex items-center justify-center flex-shrink-0">
            <FolderCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed pt-1.5">
            Confermi che le clip sono nel posto corretto del Drive?
          </p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <Button variant="secondary" onClick={() => setClipConfirm(null)} className="w-full sm:w-auto justify-center min-h-[44px]">
            Annulla
          </Button>
          <Button variant="primary" onClick={confirmClipCaricate} className="w-full sm:w-auto justify-center min-h-[44px]">
            <CheckCircle className="h-4 w-4" /> Conferma
          </Button>
        </div>
      </div>
    </AnimatedModal>
  );


  // Mese corrente: serve sia ai badge delle card (vista 1) sia alla lista
  // "Registrati" del cliente (vista 2).
  const nowVm = new Date();
  const meseCorrente = nowVm.getMonth();
  const annoCorrente = nowVm.getFullYear();
  const giornoCorrente = nowVm.getDate();
  const giorniMese = new Date(annoCorrente, meseCorrente + 1, 0).getDate();

  // ===== VISTA 1: lista clienti attivi =====
  if (!selectedClientId) {
    const clientiAttivi = state.clienti
      .filter(c => c.status === 'active')
      .sort((a, b) => a.nomeAzienda.localeCompare(b.nomeAzienda, 'it', { sensitivity: 'base' }));

    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header con stat */}
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
          <PageHeader
            title="Riprese"
            description="Contenuti pronti per la registrazione"
          />
          <StatsCard
            value={contenutiBase.length}
            label="contenuti da registrare"
            variant="violet"
          />
        </div>

        {/* Appuntamenti → calendario appuntamenti videomaker (iCloud live) */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/videomaker/appuntamenti')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600"
          >
            <CalendarClock className="w-4 h-4" />
            Appuntamenti
          </button>
          {/* Riprese già fatte: stessa pagina, vista sul passato */}
          <button
            onClick={() => navigate('/videomaker/appuntamenti?passati=1')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600"
          >
            <History className="w-4 h-4" />
            Appuntamenti passati
          </button>
        </div>

        {/* Lista clienti */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {clientiAttivi.length === 0 ? (
            <div className="col-span-full">
              <Card className="p-4 sm:p-6 md:p-8 text-center">
                <Video className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
                <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>Nessun cliente attivo</h3>
                <p className="mt-1 text-sm sm:text-base text-slate-500">I clienti attivi appariranno qui</p>
              </Card>
            </div>
          ) : (
            clientiAttivi.map((cliente) => {
              // Da registrare: tutti gli script pronti non ancora registrati del cliente
              const daRegistrare = contenutiBase.filter(c => c.clienteId === cliente.id).length;
              // Programmati questo mese (programmato/pubblicato/archiviato con data pubblicazione nel mese)
              const programmatiCliente = state.contenuti.filter(c => {
                if (c.clienteId !== cliente.id || !c.dataPubblicazione) return false;
                const d = new Date(c.dataPubblicazione);
                const questoMese = d.getMonth() === meseCorrente && d.getFullYear() === annoCorrente;
                return questoMese && (
                  c.stato === StatoContenuto.PROGRAMMATO ||
                  c.stato === StatoContenuto.PUBBLICATO ||
                  c.stato === StatoContenuto.ARCHIVIATO
                );
              }).length;
              // Obiettivo mensile raggiunto (programmati >= previsti): non è un dato
              // mostrato qui, ma decide il bordo verde e la spunta sulla card.
              const obiettivoRaggiunto = (cliente.reelMensili || 0) > 0 && programmatiCliente >= (cliente.reelMensili || 0);
              // Sollecito riprese: logica UNICA condivisa con la Dashboard
              // (valutaRiprese in utils/ripreseMatch.ts). Un reel conta come "girato"
              // solo se confermato a mano dal videomaker (videoRegistrato=true).
              // reelGiaGirati è anche il numero del badge "Registrati": quanti reel
              // il videomaker ha girato nel mese (data di registrazione) per il
              // cliente, in qualunque stato siano oggi
              // (diverso dalla Dashboard, dove il badge conta i registrati ancora da
              // programmare). Serve a capire quanti ne mancano al target mensile.
              const contenutiClienteVm = state.contenuti.filter(c => c.clienteId === cliente.id);
              const { reelGiaGirati, attesoAdOggi, inRitardoRiprese } = valutaRiprese(
                contenutiClienteVm,
                cliente.reelMensili || 0,
                meseCorrente,
                annoCorrente,
                giornoCorrente,
                giorniMese,
              );
              // Riprese già fissate sul calendario iCloud del videomaker: in tal caso
              // NON serve sollecitare nuove riprese. Stessa soppressione della Dashboard.
              const nomiAttivi = state.clienti.filter((c) => c.status === 'active').map((c) => c.nomeAzienda || '');
              const ripreseFissate = titoliAppuntamenti.some((t) =>
                appuntamentoDelCliente(cliente.nomeAzienda || '', t, nomiAttivi)
              );
              const occorronoRiprese = inRitardoRiprese && !ripreseFissate;
              return (
                <div
                  key={cliente.id}
                  onClick={() => handleSelectClient(cliente.id)}
                  className={`block cursor-pointer p-3 sm:p-4 rounded-xl border transition-all duration-200 hover:shadow-md bg-white dark:bg-gray-800 ${
                    obiettivoRaggiunto
                      ? 'border-emerald-400/70 dark:border-emerald-500/50 ring-1 ring-emerald-400/15 hover:border-emerald-400 dark:hover:border-emerald-500'
                      : occorronoRiprese
                      ? 'border-red-400/70 dark:border-red-500/50 ring-1 ring-red-400/15 hover:border-red-400 dark:hover:border-red-500'
                      : 'border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600'
                  }`}
                >
                  {occorronoRiprese && (
                    <div
                      className="flex items-center gap-1 mb-2 text-[9px] sm:text-[10px] font-semibold text-red-500 dark:text-red-400"
                      title={`Ritmo mensile in ritardo: attesi ~${attesoAdOggi} reel girati entro oggi, disponibili ${reelGiaGirati}. Occorre girare nuove riprese.`}
                    >
                      <Clapperboard className="w-2.5 h-2.5 flex-shrink-0" /> Occorrono subito delle riprese
                    </div>
                  )}
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    {/* Logo cliente */}
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 dark:bg-gray-700 flex-shrink-0 overflow-hidden">
                      {cliente.logoUrl ? (
                        <img src={cliente.logoUrl} alt={cliente.nomeAzienda} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-xs sm:text-sm font-medium">
                          {cliente.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 text-xs sm:text-sm truncate">
                        {cliente.nomeAzienda}
                      </h4>
                      <div className="flex items-center gap-1 mt-0.5 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                        <Target className="w-3 h-3 flex-shrink-0" />
                        <span><span className="font-semibold text-slate-700 dark:text-slate-300">{cliente.reelMensili || 0}</span> reel previsti</span>
                      </div>
                    </div>
                    {obiettivoRaggiunto && (
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                    )}
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    {/* Da registrare — viola: tutti gli script pronti da girare */}
                    <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200/60 dark:border-violet-800/40 px-2.5 py-1.5 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-violet-700 dark:text-violet-400 min-w-0">
                        <Clapperboard className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">Da registrare</span>
                      </div>
                      <div className="text-base sm:text-lg font-bold text-violet-600 dark:text-violet-400 leading-none flex-shrink-0">{daRegistrare}</div>
                    </div>
                    {/* Registrati — blu: reel girati nel mese per il cliente, programmati o meno.
                        Cliccabile: apre l'elenco di cosa ha girato il videomaker. */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleSelectClient(cliente.id, 'registrati'); }}
                      className="w-full rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-800/40 hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-100/70 dark:hover:bg-blue-900/40 px-2.5 py-1.5 flex items-center justify-between gap-2 transition-colors text-left"
                      title="Reel registrati questo mese (anche se già programmati): clicca per vederli"
                    >
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-blue-700 dark:text-blue-400 min-w-0">
                        <Video className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">Registrati</span>
                      </div>
                      <div className="text-base sm:text-lg font-bold text-blue-600 dark:text-blue-400 leading-none flex-shrink-0">{reelGiaGirati}</div>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {clipConfirmModal}
      </div>
    );
  }

  // ===== VISTA 2: contenuti del cliente selezionato (da registrare / registrati nel mese) =====
  const selectedClient = getClienteById(selectedClientId);
  const contenutiCliente = contenutiBase
    .filter(c => c.clienteId === selectedClientId)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  // Registrati nel mese: stesso insieme del badge blu sulla card (reelGiratiNelMese),
  // dall'ultimo segnato "Registrato".
  const registratiCliente = reelGiratiNelMese(
    state.contenuti.filter(c => c.clienteId === selectedClientId),
    meseCorrente,
    annoCorrente,
  ).sort((a, b) => new Date(b.videoRegistratoIl || 0).getTime() - new Date(a.videoRegistratoIl || 0).getTime());
  const nomeMese = nowVm.toLocaleDateString('it-IT', { month: 'long' });

  const tabClass = (attiva: boolean, colore: 'violet' | 'blue') =>
    `inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-medium border transition-colors flex-shrink-0 ${
      attiva
        ? colore === 'violet'
          ? 'bg-violet-600 border-violet-600 text-white'
          : 'bg-blue-600 border-blue-600 text-white'
        : 'bg-white dark:bg-gray-700 border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600'
    }`;

  if (vistaRegistrati) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleBackToClients}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            title="Torna ai clienti"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ letterSpacing: '-0.02em' }}>
              {selectedClient?.nomeAzienda}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {registratiCliente.length} {registratiCliente.length === 1 ? 'reel registrato' : 'reel registrati'} a {nomeMese} su {selectedClient?.reelMensili || 0} previsti
            </p>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto">
          <button type="button" onClick={() => handleCambiaVista('')} className={tabClass(false, 'violet')}>
            <Clapperboard className="w-4 h-4" /> Da registrare
            <span className="text-xs opacity-80">{contenutiCliente.length}</span>
          </button>
          <button type="button" className={tabClass(true, 'blue')}>
            <Video className="w-4 h-4" /> Registrati
            <span className="text-xs opacity-80">{registratiCliente.length}</span>
          </button>
        </div>

        {registratiCliente.length === 0 ? (
          <Card>
            <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="p-4 bg-slate-100 dark:bg-gray-700 rounded-full">
                  <Video className="h-8 w-8 sm:h-10 sm:w-10 text-slate-400 dark:text-slate-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                    Nessun reel registrato a {nomeMese}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                    Qui compaiono i contenuti che il videomaker segna come "Registrato".
                  </p>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {registratiCliente.map((contenuto) => (
              <Card
                key={contenuto.id}
                padding="none"
                className="p-3 sm:p-4 lg:p-6 transition-all hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-sm min-w-0"
              >
                <div className="flex justify-between items-center text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 mb-2 sm:mb-3">
                  <span>Aggiornato: {new Date(contenuto.updatedAt).toLocaleDateString('it-IT')}</span>
                  {contenuto.dataPubblicazione && (
                    <span>
                      {contenuto.stato === StatoContenuto.PROGRAMMATO ? 'Esce il' : 'Uscito il'}{' '}
                      {new Date(contenuto.dataPubblicazione).toLocaleDateString('it-IT')}
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight min-w-0" style={{ letterSpacing: '-0.02em' }}>
                      {contenuto.titolo}
                      <AccountDestinazioneBadge contenuto={contenuto} cliente={selectedClient} className="ml-2 align-middle" />
                    </h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                        <Video size={12} /> Registrato
                      </span>
                      <StatusBadge stato={contenuto.stato} />
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                    <span className="font-medium text-slate-900 dark:text-slate-100">Hook:</span>{' '}
                    <span className="break-words">
                      {contenuto.hook?.substring(0, 80) || 'N/A'}
                      {contenuto.hook && contenuto.hook.length > 80 && '...'}
                    </span>
                  </div>
                  <div className="pt-1 flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/videomaker/contenuto/${contenuto.id}`)}
                      className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Vedi contenuto</span>
                    </Button>
                    {contenuto.stato !== StatoContenuto.PUBBLICATO && contenuto.stato !== StatoContenuto.ARCHIVIATO && (
                      <button
                        onClick={() => riportaInRiprese(contenuto)}
                        title="Riporta il contenuto in 'Da registrare' (toglie Registrato e Montato, non tocca la programmazione)"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 w-full sm:w-auto bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-700 dark:hover:text-orange-400 hover:border-orange-200 dark:hover:border-orange-800"
                      >
                        <Undo2 className="h-4 w-4" />
                        <span>Riporta in riprese</span>
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        {clipConfirmModal}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header cliente */}
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          onClick={handleBackToClients}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          title="Torna ai clienti"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ letterSpacing: '-0.02em' }}>
            {selectedClient?.nomeAzienda}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {contenutiCliente.length} {contenutiCliente.length === 1 ? 'contenuto da registrare' : 'contenuti da registrare'}
          </p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto">
        <button type="button" className={tabClass(true, 'violet')}>
          <Clapperboard className="w-4 h-4" /> Da registrare
          <span className="text-xs opacity-80">{contenutiCliente.length}</span>
        </button>
        <button type="button" onClick={() => handleCambiaVista('registrati')} className={tabClass(false, 'blue')}>
          <Video className="w-4 h-4" /> Registrati
          <span className="text-xs opacity-80">{registratiCliente.length}</span>
        </button>
      </div>

      {/* Lista contenuti da registrare: una card per contenuto, staccate fra
          loro, così ogni girato si legge come una scheda a sé invece che come
          una riga di un elenco unico. */}
      {contenutiCliente.length === 0 ? (
        <Card>
          <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="p-4 bg-slate-100 dark:bg-gray-700 rounded-full">
                <Video className="h-8 w-8 sm:h-10 sm:w-10 text-slate-400 dark:text-slate-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                  Nessun contenuto da registrare
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                  I contenuti appariranno qui quando il copywriter li segnerà come pronti.
                </p>
              </div>
            </div>
          </div>
        </Card>
      ) : (
          <div className="space-y-3 sm:space-y-4">
            {contenutiCliente.map((contenuto) => (
              <Card
                key={contenuto.id}
                padding="none"
                className="p-3 sm:p-4 lg:p-6 transition-all hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-sm min-w-0"
              >
                {/* Timestamp */}
                <div className="flex justify-between items-center text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 mb-2 sm:mb-3">
                  <span>Creato: {new Date(contenuto.createdAt).toLocaleDateString('it-IT')}</span>
                  <span className="hidden sm:inline">Aggiornato: {new Date(contenuto.updatedAt).toLocaleDateString('it-IT')}</span>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {/* Titolo + stato */}
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight min-w-0" style={{ letterSpacing: '-0.02em' }}>
                      {contenuto.titolo}
                      <AccountDestinazioneBadge contenuto={contenuto} cliente={selectedClient} className="ml-2 align-middle" />
                    </h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <StatusBadge stato={contenuto.stato} />
                      <DriveFlagBadge contenuto={contenuto} />
                      {contenuto.clientModifiedAt && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800"
                          title={`Modificato dal cliente il ${new Date(contenuto.clientModifiedAt).toLocaleDateString('it-IT')}`}>
                          Mod. cliente
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Hook */}
                  <div className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                    <span className="font-medium text-slate-900 dark:text-slate-100">Hook:</span>{' '}
                    <span className="break-words">
                      {contenuto.hook?.substring(0, 80) || 'N/A'}
                      {contenuto.hook && contenuto.hook.length > 80 && '...'}
                    </span>
                  </div>

                  {/* Piattaforme */}
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {getClienteById(contenuto.clienteId)?.piattaforme?.map(piattaforma => (
                      <span
                        key={piattaforma}
                        className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300"
                      >
                        {piattaforma}
                      </span>
                    ))}
                  </div>

                  {/* Link ispirazione */}
                  {contenuto.linkIspirazione && (
                    <div className="text-xs sm:text-sm">
                      <span className="font-medium text-slate-700 dark:text-slate-300">Riferimento:</span>{' '}
                      <a
                        href={contenuto.linkIspirazione}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium underline"
                      >
                        Apri link
                      </a>
                    </div>
                  )}

                  {/* Indicatori media */}
                  {(contenuto.copertinaUrl || contenuto.copyPost) && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {contenuto.copertinaUrl && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                          <Image size={12} /> Copertina
                        </span>
                      )}
                      {contenuto.copyPost && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                          Copy
                        </span>
                      )}
                    </div>
                  )}

                  {/* Azioni: solo Dettagli + Registrato */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-1 sm:pt-2">
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/videomaker/contenuto/${contenuto.id}`)}
                      className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Vedi contenuto</span>
                    </Button>
                    {contenuto.stato !== StatoContenuto.PROGRAMMATO && (
                      <button
                        onClick={() => handleProgramma(contenuto)}
                        title="Mandalo in 'da programmare' per scegliere data e ora — resta comunque qui da registrare finché non lo confermi"
                        className={`flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 w-full sm:w-auto border ${
                          contenuto.prontoDaProgrammare && !contenuto.rimossoDaProgrammazione
                            ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400'
                            : 'bg-white dark:bg-gray-700 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                        }`}
                      >
                        <CalendarClock className="h-4 w-4" />
                        <span>{contenuto.prontoDaProgrammare && !contenuto.rimossoDaProgrammazione ? 'In programmazione' : 'Programma'}</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleClipCaricate(contenuto)}
                      title={contenuto.clipCaricate ? 'Clip confermate nel Drive — clicca per annullare' : 'Segna le clip come caricate nel Drive'}
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 w-full sm:w-auto ${
                        contenuto.clipCaricate
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                          : 'bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span>Clip caricate</span>
                    </button>
                    <Button
                      variant="primary"
                      disabled={!contenuto.clipCaricate}
                      onClick={() => handleMarkAsRegistrato(contenuto)}
                      title={!contenuto.clipCaricate ? 'Prima spunta "Clip caricate"' : 'Segna come registrato'}
                      className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0"
                    >
                      <Check className="h-4 w-4" />
                      <span>Registrato</span>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      {clipConfirmModal}
    </div>
  );
};

export default Videomaker;
